<template>
  <div id="app">
    <!-- Affiche un loader le temps que la connexion se fasse (très rapide) -->
    <div
      v-if="$route.name === null"
      id="app_loader"
    >
      <InfiniteLoader
        :height="64"
        :width="64"
      />
    </div>
    <div
      id="app_content"
    >
      <div id="sidebar">
        <section
          id="menu"
        >
          <NavigationMenu
            :expanded-menu="expandedSidebar"
            :dark-mode="false"
            :menu="menuToShow"
          />
        </section>
        <footer
          id="assistance"
        >
          <ButtonClassic
            v-matomo-log-click="['assistance_footer', ]"
            v-bind="btnAssistanceAttr"
            variant="special"
            color="white"
            icon="left"
          >
            <template #left-icon>
              <UilQuestionCircle />
            </template>
          </ButtonClassic>
        </footer>
      </div>
      <div id="wrapper">
        <Header
          :dark-mode="false"
          :expanded-sidebar="internalExpandedSidebar"
          @change-expanded-sidebar="internalExpandedSidebar = $event"
        />
        <main
          id="scroll_content"
          data-bs-spy="scroll"
          data-bs-target="#nav_titles"
          tabindex="0"
          @scroll="toggleHeaderScroll"
        >
          <ErrorPage
            v-if="goToErrorPage"
            :variant="errorVariant"
          />
          <template v-else>
            <router-view :key="$route.path" />
          </template>
          <span id="custom_radius" />
          <MainFooter>
            <template #left-links>
              <a
                href="https://hub.lde.fr/mentions-legales/cristal-commandes"
                target="_blank"
                rel="noreferrer noopener"
              >
                {{ $t('footer.mentions-legales') }}
              </a>
              <a
                href="https://hub.lde.fr/cgu/cristal-commandes"
                target="_blank"
                rel="noreferrer noopener"
              >
                {{ $t('footer.cgu') }}
              </a>
              <a
                :href="config.rgpdUrl"
                target="_blank"
                rel="noreferrer noopener"
              >
                {{ $t('footer.rgpd') }}
              </a>
              <button
                class="button"
                @click="showCookies = true"
              >
                {{ $t('footer.cookies') }}
              </button>
            </template>
            <template #copyright-year>
              <span>2025</span>
            </template>
          </MainFooter>
        </main>
      </div>
      <transition name="slide">
        <CookiesControl
          v-if="showCookies"
          @save="setCookiesFeatures($event)"
        />
      </transition>
    </div>
  </div>
</template>

<script>
import {
  ButtonClassic,
  InfiniteLoader,
  MainFooter,
  NavigationMenu,
} from "@lde/core_lde_vue";

import Header from "@/components/layout/Header.vue";
import CookiesControl from "@/components/layout/CookiesControl.vue";

import config from "@/config";
import { changePageTitle } from "@/modules/utils";

import menu from "@/router/menu";

import { UilQuestionCircle, } from "@iconscout/vue-unicons";

export default {
  name: "App",
  components: {
    ButtonClassic,
    CookiesControl,
    Header,
    InfiniteLoader,
    MainFooter,
    NavigationMenu,
    UilQuestionCircle,
  },
  data() {
    return {
      menu,
      isLoading: false,
      isLogged: true,
      internalExpandedSidebar: true,
      impersonator: null,
      askLinkAccount: false,
      goToErrorPage: false,
      showCookies: false,
    }
  },
  computed: {
    config() {
      return config;
    },
    btnAssistanceAttr() {
      const attr = {};
      if (this.expandedSidebar) {
        attr.label = this.$t("general.assistance");
      }
      attr.baliseType = "a";
      attr.extLink = "mailto:parents@lde.fr"
      return attr;
    },
    expandedSidebar() {
      return this.internalExpandedSidebar;
    },
    menuToShow() {
      return this.menu
        .map((m) => ({
          ...m,
          title: m.title,
        }));
    },
  },
  watch: {
    "$route.meta.title": {
      handler(title) {
        changePageTitle(title, this.$route);
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    this.$store.dispatch("login");
    // Affiche le bandeau cookies si le choix n'a pas été fait (cookie navigateur)
    if (!getCookie("lde_parents_cookie_choice")) {
      this.showCookies = true;
    }
  },
  methods: {
    /**
     * Affiche le header si on scroll vers le bas. Le cache si on scroll vers le haut.
     * Adapte aussi la hauteur du #aside_content.
     * @param {Object} event Événement natif du scroll.
     */
    toggleHeaderScroll(event) {
      const currentScrollTop = event.target.scrollTop;
      const pageHeader = document.querySelector("#page_header");

      if (pageHeader) {
        // Décale le header de sa propre taille (la hauteur peut varier selon le contenu) quand on scrolle vers le bas
        // Reset la position du header si on scrolle vers le haut
        pageHeader.style.top = currentScrollTop >= this.prevScrollPos ? `-${pageHeader.offsetHeight}px` : 0;
        this.prevScrollPos = currentScrollTop;
      }
    },
    /**
     * Définit les fonctionnalités à activer ou non selon les cookies et ferme le bandeau avec une animation.
     * @param {Object} cookies Choix de l'utilisateur sur les cookies.
     */
    setCookiesFeatures(cookies) {
      // if (cookies.technique) {
      //   Sentry.init();
      //   Sentry.setScope();
      // } else {
      //   Sentry.unsetScope();
      //   Sentry.flush();
      // }

      window._paq.push([cookies.audience ? "setConsentGiven" : "forgetConsentGiven"]);
      this.showCookies = false;
    },
  },
};

// Fonctions utilitaires pour les cookies
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  return null;
}
</script>

<style scoped>
header {
  line-height: 1.5;
}

.logo {
  display: block;
  margin: 0 auto 2rem;
}

@media (min-width: 1024px) {
  header {
    display: flex;
    place-items: center;
    padding-right: calc(var(--section-gap) / 2);
  }

  .logo {
    margin: 0 2rem 0 0;
  }
}
</style>

<style lang="scss">
@use "@/assets/styles/app.scss";
</style>